import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

/**
 * 행 높이 추정치(px) — 실측 전 스페이서 계산용. `DataTable` 의 셀 패딩(`p-3.5` = 14px ×2) + 본문 줄높이(20px)
 * ≈ 48px. 실제 높이는 `measureElement` 가 렌더 후 보정하므로 정확할 필요는 없고, **어긋나면 스크롤바 길이가
 * 잠깐 튀는 정도**다.
 */
const DEFAULT_ROW_HEIGHT = 48;

/**
 * 뷰포트 밖 선렌더 행 수 — 빠른 스크롤에서 빈 칸이 보이지 않을 만큼만. 늘릴수록 DOM 이 는다.
 *
 * 열이 많은 표에선 이 값이 **행 수가 아니라 셀 수로 곱해진다** — 19열 표에서 12면 위아래 24행 = 456셀을
 * 늘 여분으로 들고 있다. 5 면 190셀. 특정 표가 빠른 스크롤에서 빈 칸이 보이면 `overscan` prop 으로 올린다.
 */
const DEFAULT_OVERSCAN = 5;

/**
 * 스크롤러 판정용 **프로브 높이** = 뷰포트 × 이 배수.
 *
 * 폴백 스페이서는 「넘친다」를 증명하기만 하면 되므로 **추정 총높이를 그대로 띄울 이유가 없다**(1,000행이면
 * 48,000px 짜리 박스가 된다). 뷰포트보다 확실히 크기만 하면 되고, 배수를 1 초과로 두는 건 경계
 * (`scrollHeight > clientHeight + 1`)에서 판정이 흔들리지 않게 하기 위함이다.
 */
const PROBE_VIEWPORT_RATIO = 1.5;

/**
 * 세로 스크롤을 **실제로 담당하는** 조상 엘리먼트를 찾는다.
 *
 * 스크롤러가 한 곳이 아니다 — `DataTable scroll` + 바운드 높이면 표 자신의 `ScrollArea` 뷰포트가, 아니면
 * **레이아웃의 `ScrollArea` 뷰포트**(`app-layout` 이 페이지 전체 스크롤러로 쓴다 — `window` 가 아니다)가 스크롤한다.
 * 그래서 "표의 부모"나 "window" 를 고정으로 잡으면 한쪽에서 반드시 틀린다.
 *
 * ⚠️ `overflow-y` 만 보고 고르면 안 된다 — base-ui `ScrollArea` 뷰포트는 **세로가 안 넘쳐도** `overflow: scroll`
 * 이라 가짜 스크롤러를 먼저 집는다. **실제로 넘치는지**(`scrollHeight > clientHeight`)까지 확인해야 위로 올라간다.
 */
function verticalScrollParentOf(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    const scrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }
  return null; // 못 찾으면 문서 스크롤러(virtualizer 가 window 로 폴백)
}

/** base-ui `ScrollArea` 가 스크롤을 얹는 엘리먼트 — 표가 자기 뷰포트를 직접 지목할 때 쓴다. */
const VIEWPORT_SELECTOR = '[data-slot=scroll-area-viewport]';

export interface TableVirtualRowsOptions {
  /** 전체 행 수. */
  count: number;
  /** 행 높이 추정치(px). 기본 {@link DEFAULT_ROW_HEIGHT}. */
  estimateRowHeight?: number;
  /** 뷰포트 밖 선렌더 행 수. 기본 {@link DEFAULT_OVERSCAN}. */
  overscan?: number;
  /**
   * **모든 행 높이가 같은 표**임을 선언 — 행마다 붙던 실측(`measureElement`)을 끄고 **첫 행 1회 실측**으로
   * 대신한다. 행별 `ResizeObserver` 와 스크롤 프레임마다의 레이아웃 읽기가 사라진다.
   *
   * 켜도 되는 조건: 셀이 전부 **한 줄**(`whitespace-nowrap` 이거나 줄바꿈이 날 만큼 길지 않음). 문장이 들어가
   * 줄이 늘 수 있는 표(오류 내역 등)에 켜면 스크롤바 길이·위치가 어긋난다.
   *
   * 하드코딩 높이가 아니라 **실측 1회**인 이유 — `estimateRowHeight` 추정치가 틀리면 실측 보정이 없는 만큼
   * 오차가 그대로 남는다. 첫 행을 재서 쓰면 폰트·패딩이 바뀌어도 맞는다(리사이즈 때 다시 잰다).
   */
  uniformRowHeight?: boolean;
  /** 스크롤 엘리먼트 직접 지정(선택) — 자동 탐색이 못 잡는 배치에서만. */
  getScrollElement?: () => HTMLElement | null;
  /**
   * **세로 스크롤러가 표 자신의 뷰포트임이 확정**인가(= 표에 세로 높이 상한이 걸려 있는가). 켜면 아래
   * overflow 탐색을 건너뛰고 뷰포트를 곧장 지목한다.
   *
   * 탐색이 **상태에 흔들리기 때문**이다 — {@link verticalScrollParentOf} 는 `auto|scroll` 만 스크롤러로 치는데,
   * 스크롤 인계(`useScrollTakeover`)가 잠시 `overflow-y: hidden` 을 걸면 그 순간의 재탐색이 표 뷰포트를
   * 건너뛰고 **바깥 레이아웃 스크롤러를 오인**한다(리사이즈 등으로 재탐색이 돌면 그대로 굳어 하단 행이 안
   * 그려진다). 상한이 걸린 표는 스크롤러가 자명하므로 추측할 이유가 없다.
   */
  ownViewport?: boolean;
}

export interface TableVirtualRows {
  /** `<tbody>` 에 붙일 ref — 스크롤러 탐색·시작점 측정의 기준점. */
  bodyRef: React.RefObject<HTMLTableSectionElement | null>;
  /** 그릴 행 인덱스들. 스크롤러 미확정 폴백에선 전량(짧은 표) 또는 **빈 배열**(긴 표 — 스페이서만). */
  indexes: number[];
  /**
   * 위/아래 스페이서 높이(px) — **안 그린 「행들」이 차지했을** 높이. 0 이면 렌더하지 않는다.
   * 폴백 국면엔 그릴 행이 정해지지 않아 둘 다 0 이고, 대신 {@link probeHeight} 가 선다.
   */
  padTop: number;
  padBottom: number;
  /**
   * 스크롤러 판정용 **프로브 높이**(px) — 폴백에서만 >0, 정상 가상화 중엔 늘 0.
   *
   * 스페이서(`padBottom`)와 **뜻이 다르다** — 저건 실재하는 행들의 자리이고, 이건 행이 하나도 없는 상태에서
   * 「넘친다」를 만들어 보이기 위한 한 프레임짜리 임시 박스다. 둘은 상호배타라 동시에 >0 이 되지 않는다.
   */
  probeHeight: number;
  /** 실측 보정용 행 ref — 가상화 미동작 시 undefined. `<tr data-index={i} ref={measureRef}>`. */
  measureRef: ((el: HTMLElement | null) => void) | undefined;
}

/**
 * 표 본문(`tbody`) 행 가상화 — 뷰포트에 보이는 행만 그리게 인덱스·스페이서 높이를 돌려준다.
 * `SimpleTable`·`GroupedTable` 등 **표 모양이 달라도 재사용**할 수 있게, DOM 은 안 그리고 계산만 담당한다.
 *
 * **스페이서 행 방식** — `tbody` 안에서 행을 absolute 로 띄우면 표 레이아웃(열 폭 계산)이 깨지므로, 위아래에
 * 높이만 가진 빈 `tr` 을 넣어 스크롤 길이를 만든다. 그래서 열 폭 자동 계산·sticky 헤더가 그대로 산다.
 *
 * 스크롤러를 아직 못 찾았을 때(첫 프레임)의 폴백은 **추정 총높이**로 갈린다 — 뷰포트 이하면 전량, 넘으면
 * **프로브 스페이서만**(아래 폴백 분기 주석). 어느 쪽이든 `useLayoutEffect` 가 페인트 전에 스크롤러를
 * 확정하므로 눈엔 안 보인다.
 */
export function useTableVirtualRows({
  count,
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
  uniformRowHeight = false,
  getScrollElement,
  ownViewport = false,
}: TableVirtualRowsOptions): TableVirtualRows {
  // React Compiler 자동 메모 제외 — `useVirtualizer()` 가 돌려주는 함수들(`getVirtualItems`·`measureElement`)은
  // 내부 가변 상태를 읽어 **호출마다 결과가 달라져야** 한다. 컴파일러가 순수 함수로 보고 메모하면 스크롤해도
  // 같은 구간이 그려진다(TanStack 공식 권고).
  'use no memo';

  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // 스크롤러는 **마운트 후에야** 정해진다(레이아웃 스크롤러는 콘텐츠가 넘쳐야 판정된다) — state 로 들고 있다가
  // 찾으면 virtualizer 를 다시 태운다. ref 로만 두면 첫 판정 실패가 영구가 된다.
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  /**
   * 스크롤러 콘텐츠 시작점 → **행 목록 시작점**까지의 거리(px).
   *
   * 스크롤러가 표 바깥(레이아웃)이면 표 위에 헤더·차트 카드 등이 얹혀 있어 **스크롤량과 리스트 진행량이 다르다**.
   * 이 값을 안 주면 virtualizer 는 "스크롤 X = 리스트 X" 로 보고 앞 구간을 건너뛴 채 그려서, 표 상단이 빈 칸으로
   * 남고 첫 행부터 사라진다. 스크롤러가 표 자신의 뷰포트일 때도 `thead` 높이만큼 떨어져 있어 필요하다.
   */
  const [scrollMargin, setScrollMargin] = useState(0);
  /** `uniformRowHeight` 모드의 **1회 실측** 행 높이(px). null = 아직 못 잼(추정치 사용). */
  const [measuredRowHeight, setMeasuredRowHeight] = useState<number | null>(null);

  // **페인트 전**에 스크롤러를 확정해야 아래 폴백 화면(전량 렌더 / 빈 스페이서)이 한 프레임도 안 보인다.
  // 강제 동기 레이아웃이 걸리지만 폴백 DOM 이 가벼워(긴 표는 스페이서 1행) 무시할 수준이다.
  useLayoutEffect(() => {
    const sync = () => {
      const found =
        getScrollElement?.() ??
        (ownViewport
          ? (bodyRef.current?.closest<HTMLElement>(VIEWPORT_SELECTOR) ?? null)
          : verticalScrollParentOf(bodyRef.current));
      setScroller(found);
      const body = bodyRef.current;
      if (!body || !found) return setScrollMargin(0);
      // 스크롤 위치와 무관한 값이어야 하므로 현재 `scrollTop` 을 더해 **콘텐츠 좌표계**로 환산한다.
      setScrollMargin(
        body.getBoundingClientRect().top - found.getBoundingClientRect().top + found.scrollTop,
      );
    };
    // 폰트·폭 변화로 행 높이가 달라질 수 있다 — 다시 재게 비운다(이미 null 이면 setState 가 bail out).
    setMeasuredRowHeight(null);
    sync();
    // 위쪽 콘텐츠(차트 카드 등) 높이가 바뀌면 시작점도 밀린다 — 리사이즈에 다시 잰다.
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
    // 데이터가 바뀌면 높이가 변해 스크롤러 판정·시작점이 함께 뒤집힐 수 있다.
  }, [getScrollElement, ownViewport, count]);

  // eslint-disable-next-line react-hooks/incompatible-library -- `use no memo` 로 메모 제외 처리됨(위 주석)
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: useCallback(() => scroller, [scroller]),
    // 실측(1회)이 있으면 그 값 — 균일 모드에선 행별 보정이 없으므로 이 값이 곧 총높이의 정확도다.
    estimateSize: useCallback(
      () => measuredRowHeight ?? estimateRowHeight,
      [measuredRowHeight, estimateRowHeight],
    ),
    overscan,
    scrollMargin,
  });

  /**
   * 균일 모드 실측 ref — 모든 행에 붙지만 **첫 성공 이후로는 no-op**. 행별 `measureElement` 와 달리
   * `ResizeObserver` 를 남기지 않아 스크롤 중 비용이 0 이다.
   */
  const measureOnce = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const height = el.getBoundingClientRect().height;
    if (height > 0) setMeasuredRowHeight((prev) => prev ?? height);
  }, []);

  const items = virtualizer.getVirtualItems();
  const first = items[0];
  const last = items[items.length - 1];

  if (!scroller) {
    // 기준은 **행 수가 아니라 추정 총높이**다 — 행 높이가 표마다 다르므로(48px vs 100px) 개수로 자르면
    // 같은 숫자가 어떤 표에선 두 화면, 어떤 표에선 네 화면이 된다. 페이지 안 어떤 스크롤러든
    // `clientHeight <= window.innerHeight` 이므로 뷰포트 높이가 「넘칠 수 있는가」의 상한 기준이 된다.
    const estimatedTotal = count * estimateRowHeight;
    const viewport = window.innerHeight;

    // 뷰포트를 못 넘으면 **이 표 때문에 넘치는 조상은 없다** → 스크롤러가 끝내 안 잡힐 수 있고, 그때
    // 아래 프로브 경로로 두면 빈 표가 영구히 고정된다. 어차피 한 화면 분량이라 전량 렌더가 싸다.
    if (estimatedTotal <= viewport) {
      return {
        bodyRef,
        indexes: Array.from({ length: count }, (_, index) => index),
        padTop: 0,
        padBottom: 0,
        probeHeight: 0,
        measureRef: undefined,
      };
    }

    // 넘치는 표 — **콘텐츠 없이 높이만** 공급한다.
    //
    // 스크롤러 판정은 「실제로 넘치는가」를 요구할 뿐 **무엇이 넘치게 했는지는 보지 않는다**. 넘치게 하려고
    // 전 행을 그리면 첫 커밋에 표 전체가 DOM 에 올라가고(행×열 만큼의 셀) 한 틱 뒤 실행 창으로 교체하며
    // 통째로 버린다 — 조회할 때마다 반복되는 그 비용이 가상화를 무의미하게 만든다.
    //
    // 높이는 **프로브 크기면 충분**하다(총높이를 띄우면 1,000행에 48,000px 박스). 판정 정확도는 안 깎인다:
    // 높이 **무제한** 조상(가로 전용 ScrollArea 래퍼)은 프로브만큼 같이 늘어나 `scrollHeight === clientHeight`
    // 라 여전히 후보에서 빠지고, 바운드된 조상만 넘쳐서 잡힌다.
    return {
      bodyRef,
      indexes: [],
      padTop: 0,
      padBottom: 0,
      probeHeight: Math.min(estimatedTotal, viewport * PROBE_VIEWPORT_RATIO),
      measureRef: undefined,
    };
  }

  return {
    bodyRef,
    indexes: items.map((item) => item.index),
    // `start`/`end` 는 `scrollMargin` 을 포함한 스크롤러 좌표라, 표 안 높이로 쓰려면 그만큼 빼야 한다.
    padTop: first ? first.start - scrollMargin : 0,
    padBottom: last ? virtualizer.getTotalSize() - (last.end - scrollMargin) : 0,
    probeHeight: 0,
    measureRef: uniformRowHeight ? measureOnce : virtualizer.measureElement,
  };
}
